// SEQ-180 phase 2, task P21 (plan decision D21): a bucket click opens ONE
// narrower call in a PANEL, with a link to the project P&L tab. It never
// cross-filters the dashboard.
//
// Design audit, LOW: this was a centred Modal, which reads as "stop what you
// are doing" and covers the dashboard it is explaining. D21 calls it a panel,
// and the point of a drill-down is to look at a bucket WHILE the chart it
// came from is still on screen, so it is now the same right-hand drawer the
// widget editor uses, sharing that stylesheet. The behaviour is unchanged.
//
// The call goes through the same request-key machinery the dashboard uses
// (usePnlDashboardData with a one-key plan), so the drill obeys the same
// debounce, the same error handling and the same DEV fixture fallback rather
// than growing a second, subtly different fetch path.
import { useEffect, useId, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { isTopmostDialog, openDialogStack, pushDialog, removeDialog } from "../../lib/dialogStack";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Loader2, X } from "lucide-react";
import "./PnlWidgetEditor.css";
import { formatMetricValue } from "../../lib/pnl/formatMetricValue";
import type { PnlUnit } from "../../lib/pnl/pnlAnalytics";
import type { PnlWidgetConfig } from "../../lib/pnl/pnlWidgetSchema";
import { buildDrillDownRequest, type DrillDownProject, type DrillDownSource } from "../../features/pnlAnalytics/drillDown";
import { usePnlDashboardData } from "../../features/pnlAnalytics/usePnlDashboardData";
import { formatMonthLabel } from "../../features/pnlAnalytics/pnlAnalyticsFormat";
import type { PnlRequestPlan } from "../../features/pnlAnalytics/dashboardRequestKeys";
import { metricsForWidget } from "../../features/pnlAnalytics/dashboardRequestKeys";

export interface PnlDrillDownPanelProps {
  open: boolean;
  source: DrillDownSource | null;
  config: PnlWidgetConfig | null;
  projects: DrillDownProject[];
  unit: PnlUnit;
  metricName: string;
  onClose: () => void;
}

export function PnlDrillDownPanel({ open, source, config, projects, unit, metricName, onClose }: PnlDrillDownPanelProps) {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const dialogId = useId();

  // Always-latest callback ref (React review round 4), for the same reason as
  // the widget editor drawer: the caller passes an inline arrow, so naming
  // `onClose` in the dependency array rebuilt the effect on every re-render of
  // the page and bounced focus each time.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // The same focus contract the widget editor drawer keeps, and for the same
  // reason: a panel that opens without moving focus into itself is not
  // reachable by keyboard, because it is portalled to the end of the body.
  useEffect(() => {
    if (!open) return;

    pushDialog(openDialogStack, dialogId);

    const returnFocusTo = document.activeElement;
    const focusFrame = requestAnimationFrame(() => panelRef.current?.focus());

    const onKeyDown = (e: KeyboardEvent) => {
      if (!isTopmostDialog(openDialogStack, dialogId)) return;
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", onKeyDown);
      removeDialog(openDialogStack, dialogId);
      if (returnFocusTo instanceof HTMLElement) returnFocusTo.focus();
    };
    // `dialogId` is stable for this instance, so the effect fires only on a
    // real open or close, and `onClose` is no longer named inside the body.
  }, [open, dialogId]);

  const request = useMemo(() => (source ? buildDrillDownRequest(source, projects) : null), [source, projects]);

  // A one-key plan. When the panel is shut, or the drill cannot be made, the
  // plan is empty and the hook issues nothing at all.
  const plan = useMemo<PnlRequestPlan>(() => {
    if (!open || !request || !config || request.unavailableReason) {
      const empty: PnlRequestPlan = {
        keys: [],
        keyByWidgetId: {},
        comparisonKeyByWidgetId: {},
        capExceeded: false,
        droppedWidgetIds: [],
      };
      return empty;
    }
    const key = JSON.stringify(["drill", request.groupBy, request.range, request.projectIds, request.statuses]);
    const onePlan: PnlRequestPlan = {
      keys: [
        {
          key,
          groupBy: request.groupBy,
          from: request.range.from,
          to: request.range.to,
          projectIds: request.projectIds,
          statuses: request.statuses,
          metrics: metricsForWidget(config),
          isComparison: false,
        },
      ],
      keyByWidgetId: { drill: key },
      comparisonKeyByWidgetId: {},
      capExceeded: false,
      droppedWidgetIds: [],
    };
    return onePlan;
  }, [open, request, config]);

  const dataset = usePnlDashboardData(plan);

  const sorted = useMemo(() => {
    if (!config) return [];
    const rows = plan.keys.length > 0 ? (dataset.byKey[plan.keys[0].key]?.rows ?? []) : [];
    return [...rows].sort((a, b) => {
      if (a.periodMonth && b.periodMonth) return a.periodMonth.localeCompare(b.periodMonth);
      return (b.metrics[config.metric] ?? 0) - (a.metrics[config.metric] ?? 0);
    });
  }, [plan.keys, dataset.byKey, config]);

  if (!open || !request || !config) return null;

  return createPortal(
    <>
      <div className="pnl-drawer-scrim" onClick={onClose} />
      <div className="pnl-drawer" role="dialog" aria-modal="true" aria-label={request.heading} ref={panelRef} tabIndex={-1}>
        <div className="pnl-drawer-header">
          <span className="pnl-drawer-title">{request.heading}</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close the drill-down panel">
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="pnl-drawer-body">
      {request.unavailableReason ? (
        <p style={{ color: "var(--text-secondary)", lineHeight: 1.6 }}>{request.unavailableReason}</p>
      ) : dataset.loading ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "1.5rem", color: "var(--text-secondary)" }}>
          <Loader2 size={16} className="spinner" aria-hidden="true" /> Loading
        </div>
      ) : dataset.error ? (
        <p style={{ color: "#b91c1c", lineHeight: 1.6 }}>{dataset.error}</p>
      ) : sorted.length === 0 ? (
        <p style={{ color: "var(--text-secondary)" }}>No project data for this range.</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
          <thead>
            <tr>
              <th scope="col" style={headerCellStyle("left")}>
                {request.groupBy === "month" ? "Month" : "Project"}
              </th>
              <th scope="col" style={headerCellStyle("right")}>
                {metricName}
              </th>
              <th scope="col" style={headerCellStyle("right")} />
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <tr key={row.groupKey}>
                <td style={bodyCellStyle("left")}>
                  {row.periodMonth ? formatMonthLabel(row.periodMonth) : row.groupLabel}
                </td>
                <td style={bodyCellStyle("right")} data-seq-private>{formatMetricValue(row.metrics[config.metric] ?? null, unit)}</td>
                <td style={bodyCellStyle("right")}>
                  {row.projectId && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        onClose();
                        navigate(`/projects/${row.projectId}?tab=status`);
                      }}
                    >
                      <ExternalLink size={13} aria-hidden="true" /> Open P&amp;L
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

        </div>
        <div className="pnl-drawer-footer">
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}

function headerCellStyle(align: "left" | "right") {
  return {
    textAlign: align,
    padding: "8px 10px",
    borderBottom: "1px solid var(--border-color)",
    color: "var(--text-secondary)",
    fontWeight: 600,
  } as const;
}

function bodyCellStyle(align: "left" | "right") {
  return {
    textAlign: align,
    padding: "8px 10px",
    borderBottom: "1px solid var(--border-color-light)",
  } as const;
}
